import type { CennikFacety, FacetKategoria } from "@/api/schemas";
import { formatKategoria } from "@/lib/kategorie";

/*
 * Možnosti filtra kategórie z faset (KOLO 166). Bočný panel s rozbaľovačkami
 * odišiel v KOLE 199 (okno B) — filtre sú v riadku nad tabuľkou
 * (`CennikFiltre`), zoskupenie kategórií ostalo tu.
 */

/** Jedna možnosť rozbaľovačky: hodnota filtra, čitateľný názov, počet. */
export type Moznost = { hodnota: string; label: string; pocet: number };

/** Blok rozbaľovačky kategórií — samostatná možnosť alebo skupina. */
export type BlokKategorii =
  | { typ: "moznost"; moznost: Moznost }
  | { typ: "skupina"; kluc: string; nadpis: string; moznosti: Moznost[] };

/** Slovensky a čísla ako čísla — DTD 8 mm pred DTD 10 mm. */
function porovnaj(a: string, b: string): number {
  return a.localeCompare(b, "sk", { numeric: true });
}

function podlaLabelu(a: Moznost, b: Moznost): number {
  return porovnaj(a.label, b.label);
}

function moznostKategorie(k: FacetKategoria): Moznost {
  return { hodnota: k.hodnota, label: formatKategoria(k.hodnota), pocet: k.pocet };
}

/**
 * Kategórie z faset na zobrazenie (KOLO 166).
 *
 * Materiály a hrany: rovný zoznam podľa názvu. Kovanie: skupiny podľa
 * rodiča — prvá možnosť „<Skupina> — všetko" má hodnotu rodiča (backend
 * pri nej vráti rodiča aj podskupiny, preto súčet zo `skupiny`), za ňou
 * podskupiny. Skupina s jedinou kategóriou je obyčajná možnosť, kategórie
 * bez skupiny idú do „Ostatné" na koniec.
 */
export function kategorieMoznosti(
  facety: Pick<CennikFacety, "kategorie" | "skupiny">,
  zoskupit: boolean,
): BlokKategorii[] {
  if (!zoskupit) {
    return facety.kategorie
      .map(moznostKategorie)
      .sort(podlaLabelu)
      .map((moznost) => ({ typ: "moznost", moznost }));
  }

  const sucetSkupiny = new Map(facety.skupiny.map((s) => [s.hodnota, s.pocet]));
  const clenovia = new Map<string, FacetKategoria[]>();
  const ostatne: FacetKategoria[] = [];
  for (const k of facety.kategorie) {
    if (!k.skupina) {
      ostatne.push(k);
      continue;
    }
    clenovia.set(k.skupina, [...(clenovia.get(k.skupina) ?? []), k]);
  }
  // Vybraný rodič bez riadkov príde len v `skupiny` (s 0)
  for (const s of facety.skupiny) {
    if (!clenovia.has(s.hodnota)) clenovia.set(s.hodnota, []);
  }

  const bloky: { nadpis: string; blok: BlokKategorii }[] = [];
  for (const [skupina, kategorie] of clenovia) {
    // Rodičove vlastné položky sú v „— všetko", samostatne sa nevyberajú
    const deti = kategorie.filter((k) => k.hodnota !== skupina);
    const sucet =
      sucetSkupiny.get(skupina) ??
      kategorie.reduce((s, k) => s + k.pocet, 0);
    const nadpis = formatKategoria(skupina);
    const [jedineDieta] = deti;
    if (deti.length === 0) {
      bloky.push({
        nadpis,
        blok: {
          typ: "moznost",
          moznost: { hodnota: skupina, label: nadpis, pocet: sucet },
        },
      });
    } else if (kategorie.length === 1 && jedineDieta) {
      // Jediná podskupina bez rodiča — skupina by bola len prázdny obal
      const moznost = moznostKategorie(jedineDieta);
      bloky.push({ nadpis: moznost.label, blok: { typ: "moznost", moznost } });
    } else {
      bloky.push({
        nadpis,
        blok: {
          typ: "skupina",
          kluc: skupina,
          nadpis,
          moznosti: [
            { hodnota: skupina, label: `${nadpis} — všetko`, pocet: sucet },
            ...deti.map(moznostKategorie).sort(podlaLabelu),
          ],
        },
      });
    }
  }
  const vysledok = bloky
    .sort((a, b) => porovnaj(a.nadpis, b.nadpis))
    .map((b) => b.blok);
  if (ostatne.length > 0) {
    vysledok.push({
      typ: "skupina",
      kluc: "__ostatne__",
      nadpis: "Ostatné",
      moznosti: ostatne.map(moznostKategorie).sort(podlaLabelu),
    });
  }
  return vysledok;
}
